<template>
    <focus-lock :disabled="!shouldFocusLock">
        <div class="app-height flex w-full flex-row items-stretch justify-center bg-gray-200 font-roboto">
            <div
                class="
                  absolute left-[16px] top-[24px] z-10 hidden items-center

                  lg:flex

                  xl:left-[48px]
                "
            >
                <button @click.prevent="handleQuit" class="
                  focus-outline-only mr-[16px] rounded-[4px]

                  xl:mr-[40px]
                ">
                    <arrow-back-icon class="
                      size-[20px] text-gray-dark

                      xl:size-[28px]
                    " />
                </button>
                <div class="
                  mb-0 mr-[16px] font-caption text-[16px] uppercase leading-[24px] focus-outline-only

                  xl:text-[24px]
                ">
                    {{ productName }}
                </div>
                <button @click="toggleHelpPanel" class="focus-outline-only mb-[2px] rounded-[4px]">
                    <help-icon class="
                      size-[16px] text-gray-dark

                      xl:size-[20px]
                    " />
                </button>
                <div class="flex flex-1" />
            </div>

            <div
                class="
                  absolute right-[700px] top-[20px] z-10 hidden items-center font-bold

                  lg:flex

                  xl:top-[24px]
                "
            >
                <button
                    @click="executeStartOver"
                    class="
                      flex items-center gap-[8px] rounded-[4px] px-2 text-[14px] uppercase leading-[24px] text-gray-dark focus-outline-only

                      disabled:opacity-40
                    "
                    :disabled="loadedFromRecipe"
                >
                    <restart-icon class="size-[20px]" />
                    <div>
                        {{ $t('START_OVER') }}
                    </div>
                </button>
            </div>

            <div class="flex size-full flex-col overflow-hidden bg-transparent font-sans text-base tracking-normal text-gray-darkest">
                <div class="
                  flex w-full grow flex-col overflow-hidden

                  lg:flex-row lg:justify-between
                ">

                    <splitpanes horizontal v-if="isMobile">
                        <pane :min-size="20" :size="40">
                            <div class="flex h-full flex-1 flex-col">
                                <product-renderer-wrapper class="relative grow" />
                            </div>
                        </pane>
                        <pane :min-size="40">
                            <div
                                class="
                                  order-1 h-full

                                  sm:overflow-y-auto
                                "
                            >
                                <step-options-wrapper />
                            </div>
                        </pane>
                    </splitpanes>

                    <template v-else>
                        <div class="flex h-full max-h-full flex-1 flex-col">
                            <product-renderer-wrapper class="relative grow" />
                        </div>
                        <div
                            class="
                              h-full w-[650px]

                              sm:overflow-y-auto
                            "
                        >
                            <transition name="cz3__fade" mode="out-in">
                                <review-step v-if="currentStep === STEP_REVIEW" />
                                <step-options-wrapper v-else />
                            </transition>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </focus-lock>

    <help-panel />

    <overlay-panel />

    <start-over-confirmation-modal />
    <quit-confirmation-modal />
    <review-confirmation-modal />
    <review-group-text-modal />
    <enable-bulk-information-modal />
</template>

<script>
    import {
        Splitpanes,
        Pane,
    } from 'splitpanes';

    import 'splitpanes/dist/splitpanes.css';
    import FocusLock from 'vue-focus-lock';

    import allStores from '../stores/all';
    import useWidths from '../utilities/splitPanes';
    import ProductRendererWrapper from './wrappers/ProductRendererWrapper.vue';
    import StepOptionsWrapper from './wrappers/StepOptionsWrapper.vue';
    import ReviewStep from './steps/ReviewStep.vue';
    import HelpPanel from './panels/help/HelpPanel.vue';
    import OverlayPanel from './UI/OverlayPanel.vue';
    import ArrowBackIcon from './icons/ArrowBackIcon.vue';
    import HelpIcon from './icons/HelpIcon.vue';
    import RestartIcon from './icons/RestartIcon.vue';
    import { STEP_REVIEW } from '../constants';
    import StartOverConfirmationModal from './modals/StartOverConfirmationModal.vue';
    import QuitConfirmationModal from './modals/QuitConfirmationModal.vue';
    import ReviewConfirmationModal from './modals/ReviewConfirmationModal.vue';
    import ReviewGroupTextModal from './modals/ReviewGroupTextModal.vue';
    import EnableBulkInformationModal from './modals/EnableBulkInformationModal.vue';

    export default {
        mixins: [allStores],

        setup() {
            const { widths, setWidths, onResized } = useWidths([650]);
            return { widths, setWidths, onResized };
        },

        components: {
            ProductRendererWrapper,
            StepOptionsWrapper,
            ReviewStep,
            ArrowBackIcon,
            HelpIcon,
            HelpPanel,
            OverlayPanel,
            RestartIcon,
            StartOverConfirmationModal,
            QuitConfirmationModal,
            ReviewConfirmationModal,
            Splitpanes,
            Pane,
            FocusLock,
            ReviewGroupTextModal,
            EnableBulkInformationModal,
        },

        data() {
            return {
                STEP_REVIEW,
                windowWidth: window.innerWidth,
            };
        },

        computed: {
            shouldFocusLock() {
                return !this.isHelpPanelOpen
                    && !this.isStartOverConfirmationModalOpen
                    && !this.isQuitConfirmationModalOpen
                    && !this.isReviewConfirmationModalOpen;
            },

            currentStep() {
                return this.appStore.currentStep;
            },

            isHelpPanelOpen() {
                return this.customizerStore.isHelpPanelOpen;
            },
            isQuitConfirmationModalOpen() {
                return this.customizerStore.isQuitConfirmationModalOpen;
            },

            isReviewConfirmationModalOpen() {
                return this.customizerStore.isReviewConfirmationModalOpen;
            },

            isMobile() {
                return this.appStore.isMobile;
            },

            isStartOverConfirmationModalOpen() {
                return this.customizerStore.isStartOverConfirmationModalOpen;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            loadedFromRecipe() {
                return this.customizerStore.loadedFromRecipe;
            },

            productName() {
                const selected = this.viewController?.selectedAtPlacement('ProductColor');

                let name = null;

                if (selected?.custom['ridge-product-name']) {
                    name = selected?.custom['ridge-product-name'];
                }

                if (name == null) {
                    name = this.viewController?.blueprint?.custom?.['ridge-product-name'];
                }

                return name;
            },
        },

        mounted() {
            window.addEventListener('resize', this.handleResize);
        },

        beforeUnmount() {
            window.removeEventListener('resize', this.handleResize);
        },

        methods: {
            handleResize() {
                this.windowWidth = window.innerWidth;
            },

            toggleHelpPanel() {
                this.customizerStore.toggleHelpPanel();
            },

            executeStartOver() {
                this.customizerStore.toggleStartOverConfirmationModal();
            },

            handleQuit() {
                this.customizerStore.toggleQuitConfirmationModal();
            },
        },
    };
</script>

<style>
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s ease-in-out;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .splitpanes__pane {
        transition: none !important;
    }

    .splitpanes__splitter {
        position: relative;
        padding-top: 48px;
        background-color: #fff;
        border-top: 1px solid #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
    }
    .splitpanes__splitter:before {
        content: "";
        width: 56px;
        height: 5px;
        background-color: #E6E6E6;
        border-radius: 9999px;
        margin: 20px auto;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        transition: 0.4s;
    }
    /* .splitpanes--vertical > .splitpanes__splitter:before {
        left: -30px;
        right: -30px;
    }
    .splitpanes--horizontal > .splitpanes__splitter:before {
        bottom: -40px;
        z-index: 1000;
    } */
</style>
